import React from 'react';
import { Button, Space, Tag, Tooltip, Typography, message } from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  RocketOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  CopyOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

export type AgentStatus = 'draft' | 'saved' | 'active';

interface NodeToolbarProps {
  agentName: string;
  agentId?: string | null;
  status: AgentStatus;
  webhookUrl: string | null;
  saving: boolean;
  activating: boolean;
  onOpenSettings: () => void;
  onSave: () => void;
  onActivate: () => void;
  onBack?: () => void;
  readOnly?: boolean;
}

const STATUS_CONFIG: Record<AgentStatus, { color: string; label: string }> = {
  draft: { color: 'default', label: 'Borrador' },
  saved: { color: 'blue', label: 'Guardado' },
  active: { color: 'green', label: 'Activo' },
};

const NodeToolbar: React.FC<NodeToolbarProps> = ({
  agentName,
  agentId,
  status,
  webhookUrl,
  saving,
  activating,
  onOpenSettings,
  onSave,
  onActivate,
  onBack,
  readOnly = false,
}) => {
  const statusCfg = STATUS_CONFIG[status];

  return (
    <div
      style={{
        height: 56,
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        flexShrink: 0,
        zIndex: 10,
      }}
    >
      {/* Left: Back + Logo + Agent name + Status */}
      <Space size="middle" align="center">
        {onBack && (
          <Tooltip title="Volver a la lista">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              style={{ padding: '4px 8px' }}
            />
          </Tooltip>
        )}
        <ApiOutlined style={{ fontSize: 20, color: '#1677ff' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Text strong style={{ fontSize: 16, lineHeight: '20px' }}>
            {agentName || 'Nuevo Agente'}
          </Text>
          {agentId && (
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace', lineHeight: '14px' }}>
                {agentId.slice(0, 8)}…
              </Text>
              <Tooltip title="Copiar ID completo">
                <CopyOutlined
                  style={{ fontSize: 10, color: '#bfbfbf', cursor: 'pointer' }}
                  onClick={() => {
                    navigator.clipboard.writeText(agentId);
                    message.success('ID copiado');
                  }}
                />
              </Tooltip>
            </Space>
          )}
        </div>
        <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
        {status === 'active' && webhookUrl && (
          <Tooltip title={webhookUrl}>
            <Tag icon={<CheckCircleOutlined />} color="success" style={{ cursor: 'pointer' }}>
              Webhook
            </Tag>
          </Tooltip>
        )}
      </Space>

      {/* Right: Action buttons */}
      <Space size="small">
        <Tooltip title="Configuracion del agente">
          <Button
            icon={<SettingOutlined />}
            onClick={onOpenSettings}
          >
            Ajustes
          </Button>
        </Tooltip>

        {!readOnly && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={onSave}
            disabled={status === 'active'}
          >
            Guardar
          </Button>
        )}

        {!readOnly && status !== 'active' && (
          <Button
            type="primary"
            icon={<RocketOutlined />}
            loading={activating}
            onClick={onActivate}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
            disabled={status === 'draft'}
          >
            Activar
          </Button>
        )}
      </Space>
    </div>
  );
};

export default NodeToolbar;
